import { useEffect, useRef } from 'react'

const AGENTS = [
  {
    id: 'director',
    label: 'Director',
    desc: 'Sets vision & keeps the story on track.',
    color: 'purple',
    media: { type: 'video', src: '/api/prototype-assets/images/agents/Face_Animation_Video_Generated.mp4' },
  },
  {
    id: 'character-designer',
    label: 'Character Designer',
    desc: 'Creates characters & props consistently.',
    color: 'yellow',
    media: { type: 'image', src: '/prototype/images/agents/designer.png' },
  },
  {
    id: 'location-designer',
    label: 'Location Designer',
    desc: 'Designs environments & locations.',
    color: 'green',
    media: { type: 'image', src: '/prototype/images/agents/writer.png' },
  },
  {
    id: 'storyboard',
    label: 'Storyboard',
    desc: 'Plans scenes & shots for visual flow.',
    color: 'green',
    media: { type: 'image', src: '/prototype/images/agents/Storyboard.png' },
  },
  {
    id: 'animator',
    label: 'Animator',
    desc: 'Generates video with motion & continuity.',
    color: 'orange',
    media: { type: 'image', src: '/prototype/images/agents/animator.png' },
  },
  {
    id: 'sound',
    label: 'Sound',
    desc: 'Adds music & effects, mixes to finish.',
    color: 'cyan',
    media: { type: 'image', src: '/prototype/images/agents/sound.png' },
  },
]

const FEATURES = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
        <path d="M2 17l10 5 10-5"/>
        <path d="M2 12l10 5 10-5"/>
      </svg>
    ),
    text: <><strong>Specialized agents</strong> handle each craft</>,
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    text: <><strong>Coordinated workflow</strong> from idea to export</>,
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    text: <><strong>Consistent style</strong> across every element</>,
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
      </svg>
    ),
    text: <><strong>Complete output</strong> with sound &amp; music</>,
  },
]

export default function AgentsSection() {
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.2 }
    )

    const cards = sectionRef.current?.querySelectorAll('.agent-template-card')
    cards?.forEach((card, index) => {
      setTimeout(() => {
        observer.observe(card)
      }, index * 100)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <section ref={sectionRef} className="agents-section-vercel">
      <div className="agents-layout">
        {/* Left Column - Text Content */}
        <div className="agents-text-column">
          <h2 className="agents-headline">
            Meet your<br />
            <span className="gradient-text">AI production team</span>
          </h2>
          <ul className="agents-features">
            {FEATURES.map((feature, i) => (
              <li key={i}>
                {feature.icon}
                <span>{feature.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right Column - Agent Cards Grid */}
        <div className="agents-cards-grid">
          {AGENTS.map((agent) => (
            <div
              key={agent.id}
              className="agent-template-card"
              data-color={agent.color}
            >
              <div className="card-decoration" />
              <div className="card-icon">
                {agent.media.type === 'video' ? (
                  <video
                    src={agent.media.src}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    src={agent.media.src}
                    alt={agent.label}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                )}
              </div>
              <span className="card-label">{agent.label}</span>
              <p className="card-desc">{agent.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
